/**
 * Text filter for user-entered text: forbidden characters and banned words/phrases.
 *
 * SHARED WITH THE BACK END. This file is the source of truth. The layer gets a
 * generated copy (back-end/layer/nodejs/layer/textFilter.mjs) made by
 * back-end/layer/syncTextFilter.mjs, and the layer build fails if the copy is
 * out of date. Keep this file pure JavaScript: no DOM, no window, and no
 * imports other than the constants line below (the generator rewrites that
 * exact line to point at the layer's sharedConstants.mjs).
 */
import { FORBIDDEN_CHARS, BANNED_WORDS } from "./constants_v2.js";

/**
 * Normalizes one word for comparison: lowercase, with punctuation trimmed
 * from the start and end only. Inner punctuation is kept, so list entries
 * like "s.o.b" or "f-word" still match.
 * @param {string} word
 * @returns {string}
 */
const cleanToken = (word) => {
  return word
    .toLowerCase()
    .replace(/^[("'\u201C\u2018]+/, "")
    .replace(/[.,!?;:)"'\u201D\u2019]+$/, "");
};

/**
 * Splits text into cleaned words, dropping anything that cleans to empty.
 * @param {string} text
 * @returns {string[]}
 */
const toCleanTokens = (text) => {
  return text.split(/\s+/).map(cleanToken).filter(Boolean);
};

// Build lookups once. List entries are cleaned exactly like user text.
const BANNED_SINGLE_WORDS = new Set();
const BANNED_PHRASES_BY_FIRST_WORD = new Map(); // first word -> array of token arrays
for (const entry of BANNED_WORDS) {
  const tokens = toCleanTokens(entry);
  if (tokens.length === 0) continue;
  if (tokens.length === 1) {
    BANNED_SINGLE_WORDS.add(tokens[0]);
  } else {
    if (!BANNED_PHRASES_BY_FIRST_WORD.has(tokens[0])) {
      BANNED_PHRASES_BY_FIRST_WORD.set(tokens[0], []);
    }
    BANNED_PHRASES_BY_FIRST_WORD.get(tokens[0]).push(tokens);
  }
}

/**
 * Finds banned single words and banned phrases in a list of cleaned tokens.
 * Phrases match whole words in order ("2 girls 1 cup" matches "2 Girls, 1 cup!"),
 * never parts of words ("class" never matches "ass").
 * @param {string[]} tokens - from toCleanTokens()
 * @returns {{start:number, length:number, entry:string}[]}
 */
const findBannedMatches = (tokens) => {
  const matches = [];
  for (let i = 0; i < tokens.length; i++) {
    if (BANNED_SINGLE_WORDS.has(tokens[i])) {
      matches.push({ start: i, length: 1, entry: tokens[i] });
    }
    const phrases = BANNED_PHRASES_BY_FIRST_WORD.get(tokens[i]) || [];
    for (const phrase of phrases) {
      if (i + phrase.length > tokens.length) continue;
      let isMatch = true;
      for (let j = 1; j < phrase.length; j++) {
        if (tokens[i + j] !== phrase[j]) {
          isMatch = false;
          break;
        }
      }
      if (isMatch) {
        matches.push({ start: i, length: phrase.length, entry: phrase.join(" ") });
      }
    }
  }
  return matches;
};

// Longest run of the same character allowed in a row (e.g. "!!!!!!!!!!" is ok,
// one more "!" is not). Basic spam protection.
export const MAX_REPEATED_CHARS = 10;

/**
 * True if the text has more than MAX_REPEATED_CHARS of the same character in a row.
 * Spaces count too.
 * @param {string} text
 * @returns {boolean}
 */
export const hasExcessiveRepeatedChars = (text) => {
  let count = 0;
  let lastChar = "";
  for (const char of text) {
    count = char === lastChar ? count + 1 : 1;
    lastChar = char;
    if (count > MAX_REPEATED_CHARS) {
      return true;
    }
  }
  return false;
};

/**
 * Returns the forbidden characters (from FORBIDDEN_CHARS) found in the text.
 * @param {string} text
 * @returns {string[]}
 */
export const findForbiddenChars = (text) => {
  return FORBIDDEN_CHARS.filter((char) => text.includes(char));
};

/**
 * Returns the banned words and phrases (from BANNED_WORDS) found in the text.
 * @param {string} text
 * @returns {string[]}
 */
export const findBannedWords = (text) => {
  return findBannedMatches(toCleanTokens(text)).map((match) => match.entry);
};

/**
 * Cleans text written by someone else before it is shown: forbidden
 * characters and banned words/phrases are replaced with "...".
 * This is a display safeguard. Always render the result with textContent
 * (front end) or escapeHtml (back end) as well.
 * @param {string} text
 * @returns {string}
 */
export const sanitizeText = (text) => {
  let sanitized = String(text);
  for (const char of FORBIDDEN_CHARS) {
    sanitized = sanitized.replaceAll(char, "...");
  }

  // Work on the original words so the output keeps the writer's spelling,
  // but match against the cleaned versions.
  const words = sanitized.split(/\s+/).filter(Boolean);
  const cleaned = words.map(cleanToken);

  // Map cleaned-token positions back to word positions (some words clean to "")
  const wordIndexOfToken = [];
  cleaned.forEach((token, wordIndex) => {
    if (token) wordIndexOfToken.push(wordIndex);
  });
  const tokens = cleaned.filter(Boolean);

  const hidden = new Array(words.length).fill(false);
  for (const match of findBannedMatches(tokens)) {
    for (let k = match.start; k < match.start + match.length; k++) {
      hidden[wordIndexOfToken[k]] = true;
    }
  }

  // Replace each run of hidden words with a single "..."
  const output = [];
  for (let i = 0; i < words.length; i++) {
    if (hidden[i]) {
      if (!hidden[i - 1]) output.push("...");
    } else {
      output.push(words[i]);
    }
  }
  return output.join(" ");
};

// Letters, including accented Latin letters (but not × or ÷).
const LETTER = "A-Za-z\\u00C0-\\u00D6\\u00D8-\\u00F6\\u00F8-\\u00FF";
// The name part of "namePart (USERID)": starts with a letter, then letters,
// digits, apostrophes, periods and hyphens, with single spaces between words.
// No parentheses, so nobody can name themselves "Jay (SOMEONE'S ID)".
const NAME_PART_PATTERN = new RegExp(
  `^[${LETTER}][${LETTER}0-9'.\\-]*(?: [${LETTER}0-9'.\\-]+)*$`,
  "u",
);

/**
 * True if the text is a well-formed name part (see NAME_PART_PATTERN).
 * Length and banned words are checked separately.
 * @param {string} text
 * @returns {boolean}
 */
export const isNamePartWellFormed = (text) =>
  typeof text === "string" && NAME_PART_PATTERN.test(text);

// Search text: the same characters as names, any of them first.
const SEARCH_PATTERN = new RegExp(
  `^[${LETTER}0-9'.\\-]+(?: [${LETTER}0-9'.\\-]+)*$`,
  "u",
);

/**
 * True if the text only uses characters that can appear in names.
 * @param {string} text
 * @returns {boolean}
 */
export const isSearchWellFormed = (text) =>
  typeof text === "string" && SEARCH_PATTERN.test(text);

/**
 * True if the text has control characters (newlines, tabs, NUL, ...).
 * Free text like blurbs and security questions can't contain them.
 * @param {string} text
 * @returns {boolean}
 */
export const hasControlChars = (text) => /[\u0000-\u001F\u007F]/.test(text);

export const textFilter = {
  findForbiddenChars,
  findBannedWords,
  sanitizeText,
  hasExcessiveRepeatedChars,
  isNamePartWellFormed,
  isSearchWellFormed,
  hasControlChars,
};
